import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  itemVariants,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type TemplateMissmatchAlertDialogProps = {
  onReplaceFile: () => void;
  onDiscardTemplate: () => void;
  onClose: () => void;
};

const ANSWERS = {
  replaceFile: 'replaceFile',
  discardTemplate: 'discardTemplate',
};

export const TemplateMissmatchAlertDialog = ({
  onClose,
  onReplaceFile,
  onDiscardTemplate,
}: TemplateMissmatchAlertDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const [selectedAnswer, setSelectedAnswer] = useState(ANSWERS.replaceFile);

  const handleSubmit = () => {
    if (selectedAnswer === ANSWERS.replaceFile) {
      onReplaceFile();
    }

    if (selectedAnswer === ANSWERS.discardTemplate) {
      onDiscardTemplate();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center justify-center gap-2">
              <WarningAltFilled className="size-5 fill-warning-main" />
              <p>{t("Template's headers mismatch")}</p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex-col">
            <DialogDescription>
              {t('The headers in the uploaded file do not match the template')}
            </DialogDescription>
            <RadioGroup
              value={selectedAnswer}
              onValueChange={setSelectedAnswer}
              className="mt-6 flex flex-col gap-2"
            >
              <Label
                data-dd-action-name="Upload another file and keep the template"
                variant="bodyShort01"
                className={cn(
                  itemVariants({
                    selectionListItem:
                      selectedAnswer === ANSWERS.replaceFile
                        ? 'selected'
                        : 'default',
                  }),
                  'm-0 cursor-pointer shadow-sm',
                )}
              >
                <RadioGroupItem value={ANSWERS.replaceFile} />
                <span>{t('Upload another file and keep the template')}</span>
              </Label>
              <Label
                data-dd-action-name="Keep file and do not use template"
                variant="bodyShort01"
                className={cn(
                  itemVariants({
                    selectionListItem:
                      selectedAnswer === ANSWERS.discardTemplate
                        ? 'selected'
                        : 'default',
                  }),
                  'm-0 cursor-pointer shadow-sm',
                )}
              >
                <RadioGroupItem value={ANSWERS.discardTemplate} />
                <span>
                  {t('Keep the following file and do not use the template')}
                </span>
              </Label>
            </RadioGroup>
          </DialogContent>
          <DialogFooter>
            <Button variant="text" size="large" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button size="large" color="warning" onClick={handleSubmit}>
              {t('Proceed')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
